import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserClients } from "../services/apiServices";
import Loader from "../Components/common/Loader";
import { useAuth } from "./AuthProvider";
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";

const ClientContext = createContext({});

// Top-level path segments that are not client ids and must never be treated
// as one (they have their own routes).
const RESERVED_SEGMENTS = new Set(["login-callback"]);

export function ClientProvider({ children }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const { authState } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // The client id lives in the first path segment: /:clientId/c/:conversationId.
  // ClientsProvider renders outside <Routes>, so we read it from the pathname
  // directly rather than via useParams.
  const pathClientId = useMemo(() => {
    const segment = location.pathname.split("/").filter(Boolean)[0];
    if (!segment || RESERVED_SEGMENTS.has(segment)) return null;
    return decodeURIComponent(segment);
  }, [location.pathname]);

  const {
    data: clients = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await getUserClients();
      return res?.data ?? [];
    },
    enabled: authState?.isAuthenticated,
    placeholderData: [],
    staleTime: Infinity,
    refetchOnMount: false,
  });

  const { data: kcSelectedClient } = useQuery({
    queryKey: ["selected_client"],
    queryFn: async () => {
      // Post message to parent and get the selected client as message send back from parent
      return new Promise((resolve) => {
        const handleMessage = (event) => {
          if (event.data?.type === "set-selected-client") {
            console.log(
              "Received selected client from parent:",
              event.data.clientId,
            );
            resolve(event.data.clientId);
            window.removeEventListener("message", handleMessage);
          }
        };
        window.addEventListener("message", handleMessage);
        // Ask parent for selected client
        window.parent.postMessage({ type: "get-selected-client" }, "*");
      });
    },
    enabled: authState?.isAuthenticated,
    staleTime: Infinity,
    refetchOnMount: false,
  });

  const shouldShowClientSelector = useMemo(() => {
    // If there is no clients or only one client, then we should not show the dropdown
    if (!clients || clients.length < 2) return false;
    // If we have a selected client from kc, then we should not show the dropdown, because the client is already selected from KC side
    if (kcSelectedClient && selectedClient) return false;
    return clients.length > 1;
  }, [clients, kcSelectedClient, selectedClient]);

  // Resolve the selected client with a fixed precedence:
  //   1. KC parent-supplied client (when embedded, the parent wins).
  //   2. The /:clientId path segment, when it maps to a client the user has.
  //      This is what makes a page refresh restore the right client so the
  //      conversation fetch doesn't mismatch the URL's conversationId.
  //   3. The default client (or the first available).
  useEffect(() => {
    if (_.isEmpty(clients)) {
      return;
    }

    if (kcSelectedClient) {
      const kcClient = clients.find((c) => c.externalId === kcSelectedClient);
      if (kcClient) {
        setSelectedClient(kcClient);
        return;
      }
    }

    if (pathClientId) {
      const pathClient = clients.find((c) => String(c.id) === pathClientId);
      if (pathClient) {
        setSelectedClient(pathClient);
        return;
      }
    }

    const fallback = clients.find((item) => item.isDefault) ?? clients[0];
    setSelectedClient(fallback);
  }, [clients, kcSelectedClient, pathClientId, setSelectedClient]);

  // Keep the URL's /:clientId segment in sync with the resolved client. When
  // the path's client doesn't match (root "/", an unknown/stale client id, or
  // a KC-driven override), redirect to that client's landing path. When it
  // already matches, do nothing so any /c/:conversationId subpath is preserved
  // on refresh. `replace` avoids polluting the browser history.
  useEffect(() => {
    if (!selectedClient?.id) {
      return;
    }
    if (location.pathname === "/login-callback") {
      return;
    }
    const id = String(selectedClient.id);
    if (pathClientId === id) {
      return;
    }
    navigate(`/${encodeURIComponent(id)}`, { replace: true });
  }, [selectedClient, pathClientId, location.pathname, navigate]);

  const value = {
    clients,
    selectedClient,
    shouldShowClientSelector,
    loading: isLoading || isFetching,
    selectedClientId: selectedClient?.id,
    setSelectedClient,
  };

  if (isLoading || (isFetching && clients.length === 0)) {
    return <Loader />;
  }

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
}

export const useClient = () => useContext(ClientContext);
export const useClientId = () => {
  const context = useContext(ClientContext);
  return context?.selectedClient?.id;
};
